export type AuthDisconnectReason =
  | 'unauthenticated'
  | 'token-expired'
  | 'forbidden'
  | 'unknown';

export type AuthDisconnectEvent = {
  reason: AuthDisconnectReason;
  status?: number;
  url?: string;
  at: number;
};

type Listener = (e: AuthDisconnectEvent) => void;

const listeners = new Set<Listener>();
let lastEventAt = 0;

/**
 * Once a disconnect event has been processed, all subsequent events are
 * suppressed until `resetAuthDisconnected()` is called (on successful login).
 * This prevents the "popup loop" where stale React Query refetches, health
 * checks, and session-expiry ticks keep re-triggering the disconnect flow.
 */
let disconnected = false;

export function isAuthDisconnected(): boolean {
  return disconnected;
}

export function resetAuthDisconnected(): void {
  disconnected = false;
  lastEventAt = 0;
}

type WireMsg =
  | { v: 1; t: 'auth:disconnected'; src: string; at: number; evt: AuthDisconnectEvent };

const CHANNEL = 'pacmaster:auth';

function makeTabId(): string {
  try {
    // crypto.randomUUID is supported in modern browsers
    // eslint-disable-next-line no-undef
    return (globalThis as any).crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
}

const TAB_ID = makeTabId();
let wired = false;
let bc: BroadcastChannel | null = null;

function ensureWired() {
  if (wired) return;
  wired = true;

  // Browser-only; no-op in SSR.
  if (typeof window === 'undefined') return;
  if (typeof BroadcastChannel === 'undefined') return;

  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (ev: MessageEvent) => {
      const msg = ev?.data as WireMsg | undefined;
      if (!msg || msg.v !== 1) return;
      if (msg.src === TAB_ID) return;
      if (msg.t !== 'auth:disconnected') return;

      // Re-emit locally (dedupe protection still applies).
      notifyAuthDisconnected(msg.evt);
    };
  } catch {
    bc = null;
  }
}

function broadcast(evt: AuthDisconnectEvent) {
  ensureWired();
  try {
    bc?.postMessage({ v: 1, t: 'auth:disconnected', src: TAB_ID, at: Date.now(), evt } satisfies WireMsg);
  } catch {
    // ignore
  }
}

export function subscribeAuthDisconnected(fn: Listener): () => void {
  ensureWired();
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function notifyAuthDisconnected(e?: Partial<AuthDisconnectEvent>) {
  ensureWired();

  // Already handled — suppress all further events until reset (login success).
  if (disconnected) return;

  const at = Date.now();
  // De-dupe bursts (multiple requests failing at once)
  if (at - lastEventAt < 1500) return;
  lastEventAt = at;

  // Lock out further events for this tab.
  disconnected = true;

  const evt: AuthDisconnectEvent = {
    reason: e?.reason ?? 'unauthenticated',
    status: e?.status,
    url: e?.url,
    at,
  };

  // Fan out to other tabs so they converge on the same auth state.
  broadcast(evt);

  for (const fn of Array.from(listeners)) {
    try { fn(evt); } catch { /* ignore */ }
  }
}
